import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import TableEditor from './TableEditor';
import PDFViewer from './PDFViewer';
import ImageViewer from './ImageViewer';
import QuickDrawEditor from './QuickDrawEditor';
import ThemeService from '../services/ThemeService';
import UploadImageDialog from './UploadImageDialog';
import WorkspaceSettingsDialog from './WorkspaceSettingsDialog';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { 
  ChevronUp,
  ChevronDown,
  Save,
  Download,
  ArrowLeft,
  Table,
  Plus,
  Trash2,
  Merge,
  Moon,
  Sun,
  Ban,
  X,
  Flame,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  RefreshCcw,
  Settings 
} from 'lucide-react';

const Workspace = ({ isShared = false }) => {
  const { projectId, fileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const [isAuthenticated, setIsAuthenticated] = useState(!isShared);
  // const [isDarkMode, setIsDarkMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(ThemeService.getCurrentTheme() === 'dark');
  const [activeCell, setActiveCell] = useState({ row: null, col: null });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [tables, setTables] = useState([]);
  const [currentTableData, setCurrentTableData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [highlights, setHighlights] = useState({});
  const [showHighlightMenu, setShowHighlightMenu] = useState(null);
  const [pdfViewerInstance, setPdfViewerInstance] = useState(null);
  const [editingCell, setEditingCell] = useState({ row: null, col: null });
  const [selectedCell, setSelectedCell] = useState({ row: null, col: null });
  const [formulaBarValue, setFormulaBarValue] = useState('');
  const [tableName, setTableName] = useState('');
  const [isEditingTableName, setIsEditingTableName] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [quickDrawMode, setQuickDrawMode] = useState(false);
  const [quickDrawKey, setQuickDrawKey] = useState(0);
  const [quickDrawLoading, setQuickDrawLoading] = useState(false);
  const [confidenceThreshold, setConfidenceThreshold] = useState(80);
  const [showThresholdSlider, setShowThresholdSlider] = useState(false);
  const [tempThreshold, setTempThreshold] = useState(80);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showUploadImageDialog, setShowUploadImageDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [includeBlankCells, setIncludeBlankCells] = useState(false); // Off by default
  const [autoHideThumbnailBar, setAutoHideThumbnailBar] = useState(true);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showRowOptions, setShowRowOptions] = useState(false);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const rowOptionsRef = useRef(null);
  const columnOptionsRef = useRef(null);
  const [rowOptionsHovered, setRowOptionsHovered] = useState(false);
  const [columnOptionsHovered, setColumnOptionsHovered] = useState(false);
  // Removed showFileSelectModal - using inline dropdown now
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [collectionFiles, setCollectionFiles] = useState([]);
  const [isFileSwitching, setIsFileSwitching] = useState(false);
  
  // Optimistic loading states for better UX
  const [isLoadingFileUrl, setIsLoadingFileUrl] = useState(false);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ step: '', progress: 0 });
  const fileDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const saveInProgressRef = useRef(false);
    const [currentSelection, setCurrentSelection] = useState({
    range: {
      start: { row: null, col: null },
      end: { row: null, col: null }
    },
    selectedCells: []
  });
  // New state for viewer collapse functionality
  const [viewerCollapsed, setViewerCollapsed] = useState(false);
  const [includeOriginalImages, setIncludeOriginalImages] = useState(false); // New state for image export setting
  
  const { jumpToPage } = pageNavigationPluginInstance;

  // Add this to Workspace.js, near the top of the component
  useEffect(() => {
    // console.log("Current selection changed:", {
    //   currentSelection,
    //   selectedRow: currentSelection?.range?.start?.row,
    //   isFalsy: !currentSelection?.range?.start?.row,
    //   isNull: currentSelection?.range?.start?.row === null,
    //   isUndefined: currentSelection?.range?.start?.row === undefined
    // });
  }, [currentSelection]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedIncludeOriginalImages = localStorage.getItem('includeOriginalImages');
    if (savedIncludeOriginalImages !== null) {
      setIncludeOriginalImages(savedIncludeOriginalImages === 'true');
    }
  }, []);

  const updateIncludeOriginalImages = (value) => {
    setIncludeOriginalImages(value);
    localStorage.setItem('includeOriginalImages', value);
  };

  // Generate standardized filename for table exports
  const generateStandardFileName = (tableData, tableIndex, format = 'csv') => {
    const projectName = (projectData?.name || 'Project').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
    
    let pageNumber;
    if (projectData?.type === 'pdf') {
      pageNumber = tableData.pageNumber || 1;
    } else {
      // For snippet projects, find the image index
      const currentFile = projectData?.files?.find(f => 
        f.tables?.some(t => t.id === tableData._id || t.id === selectedTable)
      );
      pageNumber = currentFile ? imageUrls.findIndex(img => img.id === currentFile.id) + 1 : 1;
    }
    
    return `${projectName}_Page${pageNumber}_Table${tableIndex}_${projectId}.${format}`;
  };

  // Function to download files from URLs
  const downloadFileFromUrl = async (url, fileName) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch ${fileName}`);
      
      const blob = await response.blob();
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(`Error downloading ${fileName}:`, error);
      throw error;
    }
  };

  
  // Add table data cache to prevent redundant fetches
  const tableDataCache = useRef(new Map());
  const loadingPromises = useRef(new Map());

  // Security and validation helpers
  const validateProjectId = (id) => {
    // Basic validation: ensure it's a non-empty string that looks like an ID
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new Error('Invalid project ID');
    }
    // Add more specific validation based on your ID format
    return id.trim();
  };

  const validateFileId = (id) => {
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new Error('Invalid file ID');
    }
    return id.trim();
  };

  const validateTableId = (id) => {
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new Error('Invalid table ID');
    }
    return id.trim();
  };

  const handleApiError = (error, context) => {
    console.error(`API Error in ${context}:`, error);
    
    // Security: Don't expose internal error details to user
    let userMessage = 'An unexpected error occurred. Please try again.';
    
    if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      userMessage = 'Access denied. Please check your permissions.';
    } else if (error.message?.includes('404') || error.message?.includes('Not Found')) {
      userMessage = 'The requested content was not found.';
    } else if (error.message?.includes('500') || error.message?.includes('Internal Server')) {
      userMessage = 'Server error. Please try again later.';
    } else if (error.message?.includes('Failed to fetch')) {
      userMessage = 'Network error. Please check your connection.';
    }
    
    setError(userMessage);
    return userMessage;
  };

  useEffect(() => {
    const fetchProjectDataOptimized = async () => {
      if (!projectId) {
        console.log('No projectId provided, skipping fetch');
        return;
      }
    
      try {
        // Validate inputs for security
        const validatedProjectId = validateProjectId(projectId);
        if (fileId) {
          validateFileId(fileId);
        }
        console.log('Starting optimized parallel loading for project:', projectId);
        setLoading(true);
        setLoadingProgress({ step: 'Loading project...', progress: 20 });
        
        // Create parallel promises array
        const promises = [];
        const promiseNames = [];
        
        // 1. NEW ARCHITECTURE: Fetch ONLY the specific file data needed
        if (fileId) {
          // Load single file details directly (much faster!)
          promises.push(fetch(`${process.env.REACT_APP_API_URL}/api/files/${fileId}/details`));
          promiseNames.push('fileDetails');
        } else {
          // Fallback: Load lightweight project navigation data
          promises.push(fetch(`${process.env.REACT_APP_API_URL}/api/projects/${validatedProjectId}/navigation`));
          promiseNames.push('projectNavigation');
        }
        
        // Execute all promises in parallel with security validation
        const responses = await Promise.allSettled(promises);
        
        // Validate and process data (either file details or project navigation)
        const dataResponse = responses[0];
        if (dataResponse.status === 'rejected' || !dataResponse.value.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const responseData = await dataResponse.value.json();
        
        // Security validation: ensure user has access to this data
        if (!responseData || !responseData.id) {
          throw new Error('Invalid data received');
        }
        
        console.log('Data loaded:', {
          id: responseData.id,
          type: responseData.type,
          filesCount: responseData.files?.length || 0,
          isFileSpecific: !!fileId,
          hasCollection: !!responseData.collection
        });

        setProjectData(responseData);

        // Set collection information if available
        if (responseData.collection) {
          setCurrentCollection(responseData.collection);
          setCollectionFiles(responseData.collectionFiles || []);
        } else {
          setCurrentCollection(null);
          setCollectionFiles([]);
        }
        setLoadingProgress({ step: 'Loading files...', progress: 50 });
        
        // Find current file and prepare for parallel loading
        if (responseData.files && responseData.files.length > 0) {
          const currentFile = fileId 
            ? responseData.files.find(f => f.id === fileId)
            : responseData.files[0];
            
          if (!currentFile) {
            console.warn('Current file not found, using first file');
            const fallbackFile = responseData.files[0];
            // Note: setCurrentFile is not defined in state, using currentFile variable instead
          }
          
          // Process tables with minimal data first
          if (currentFile?.tables?.length > 0) {
            const sortedTables = [...currentFile.tables].sort((a, b) => {
              if (a.pageNumber !== b.pageNumber) {
                return a.pageNumber - b.pageNumber;
              }
              return 0;
            });
            setTables(sortedTables);
            setSelectedTable(sortedTables[0].id);
            
            // Load file URL first (faster), then table data
            setLoadingProgress({ step: 'Loading file...', progress: 60 });
            await loadCurrentFileUrl(responseData, currentFile);
            
            // Then load table data separately
            setLoadingProgress({ step: 'Loading table data...', progress: 85 });
            await loadTableDataCached(sortedTables[0].id);
            
            setLoadingProgress({ step: 'Complete!', progress: 100 });
            setIsInitialLoad(false); // Enable normal effect behavior
          } else {
            // No tables, just load file URL
            await loadCurrentFileUrl(responseData, currentFile);
            setIsInitialLoad(false); // Enable normal effect behavior
          }
        }
    
      } catch (err) {
        handleApiError(err, 'fetchProjectDataOptimized');
      } finally {
        setLoading(false);
        setLoadingProgress({ step: '', progress: 0 });
      }
    };
    
    fetchProjectDataOptimized();
  }, [projectId, fileId]);

  const handleImagesUploaded = async (response) => {
    console.log('Images uploaded successfully:', response);
    
    if (response.files && response.files.length > 0) {
      // Refresh project data to include the new files
      if (projectId) {
        try {
          // Use the appropriate endpoint based on context
          const endpoint = fileId 
            ? `${process.env.REACT_APP_API_URL}/api/files/${fileId}/details`
            : `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/navigation`;
            
          const projectResponse = await fetch(endpoint);
          if (!projectResponse.ok) {
            throw new Error('Failed to fetch updated project data');
          }
          
          const updatedProject = await projectResponse.json();
          setProjectData(updatedProject);
          
          // Add new images to imageUrls array (they'll be in pending state)
          const newImageUrls = response.files.map(file => ({
            id: file.id,
            url: null, // Will be fetched when needed
            name: file.name,
            sequence: file.sequence || 0,
            status: 'pending'
          }));
          
          setImageUrls(prev => [...prev, ...newImageUrls]);
          
          // Show a success notification
          alert(`${response.files.length} image(s) added successfully! They will be processed shortly.`);
        } catch (error) {
          console.error('Error refreshing project data:', error);
        }
      }
    }
  };
  
  // 4. Add a fetchFileStatus function to check image processing status
  const fetchFileStatus = async (fileId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/files/${fileId}/status`);
      if (!response.ok) {
        throw new Error('Failed to fetch file status');
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching status for file ${fileId}:`, error);
      return null;
    }
  };
  
  // Updated pollFileStatus function to include file type information
  const pollFileStatus = async (fileId, attempts = 0) => {
    if (attempts > 20) {
      console.log('Polling timeout for file:', fileId);
      pollingFilesRef.current.delete(fileId); // Clean up tracking
      return; // Give up after 20 attempts (100 seconds)
    }
    
    try {
      const status = await fetchFileStatus(fileId);
      
      if (status && (status.processingStatus === 'completed' || status.processingStatus === 'failed')) {
        console.log('File processing finished:', fileId, 'Status:', status.processingStatus);
        pollingFilesRef.current.delete(fileId); // Clean up tracking
        
        // Update image URLs with the new status
        setImageUrls(prev => prev.map(img => 
          img.id === fileId 
            ? { 
                ...img, 
                status: status.processingStatus,
                fileType: status.fileType || img.fileType,
                displayType: status.displayType || status.fileType || img.fileType,
                hasConvertedVersion: status.hasConvertedVersion || false
              } 
            : img
        ));
        
        // If completed, fetch the file URL
        if (status.processingStatus === 'completed') {
          const urlResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/files/${fileId}/url`);
          if (urlResponse.ok) {
            const urlData = await urlResponse.json();
            setImageUrls(prev => prev.map(img => 
              img.id === fileId 
                ? { 
                    ...img, 
                    url: urlData.url,
                    isConverted: urlData.isConverted || false
                  } 
                : img
            ));
          }
          
          // Refresh project data to get tables - use appropriate endpoint
          const endpoint = fileId 
            ? `${process.env.REACT_APP_API_URL}/api/files/${fileId}/details`
            : `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/navigation`;
            
          const projectResponse = await fetch(endpoint);
          if (projectResponse.ok) {
            const updatedProject = await projectResponse.json();
            setProjectData(updatedProject);
          }
        }
      } else {
        // Still processing, poll again after a delay
        setTimeout(() => pollFileStatus(fileId, attempts + 1), 5000); // 5 second intervals
      }
    } catch (error) {
      console.error(`Error polling status for file ${fileId}:`, error);
    }
  };

  // Track which files are already being polled to prevent duplicate polling
  const pollingFilesRef = useRef(new Set());

  useEffect(() => {
    // Look for pending images and start polling their status (only if not already polling)
    imageUrls
      .filter(img => img.status === 'pending' && !pollingFilesRef.current.has(img.id))
      .forEach(img => {
        pollingFilesRef.current.add(img.id);
        console.log('Starting to poll file:', img.id);
        pollFileStatus(img.id);
      });
  }, [imageUrls]);

  // Update the toggle function:
  const toggleTheme = () => {
    const newTheme = ThemeService.toggleTheme();
    setIsDarkMode(newTheme === 'dark');
  };

  // Add effect to listen for theme changes:
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === ThemeService.STORAGE_KEY) {
        setIsDarkMode(ThemeService.getCurrentTheme() === 'dark');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Add this effect to handle closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setShowExportDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add effect to close these dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rowOptionsRef.current && !rowOptionsRef.current.contains(e.target)) {
        setShowRowOptions(false);
      }
      if (columnOptionsRef.current && !columnOptionsRef.current.contains(e.target)) {
        setShowColumnOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openQuickDrawEditor = async () => {
    if (!selectedTable) return;
    
    // Start loading
    setQuickDrawLoading(true);
    
    try {
      // Fetch fresh table data from server
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${selectedTable}`);
      if (!response.ok) throw new Error('Failed to fetch table data');
      
      const freshTableData = await response.json();
      
      // Update the table data with a smooth transition
      setCurrentTableData(prevData => {
        // Preserve user edits to cell content by merging
        return {
          ...freshTableData,
          currentData: prevData.currentData // Keep current edits
        };
      });
      
      // Update highlights from the fresh data
      const highlightMap = {};
      freshTableData.structure?.highlights?.forEach(h => {
        highlightMap[`${h.row}-${h.col}`] = h.color;
      });
      setHighlights(highlightMap);
      
      // After a short delay to allow state updates, open the editor
      setTimeout(() => {
        setQuickDrawMode(true);
        setQuickDrawLoading(false);
      }, 300);
      
    } catch (error) {
      console.error('Error refreshing table data:', error);
      setQuickDrawLoading(false);
      // Show an error toast or message
      alert('Failed to refresh table data. Please try again.');
    }
  };

  // Add the exportAllTables function
  // In Workspace.js, replace the exportAllTables function with this improved version:

  // Replace the exportAllTables function with this simplified version:

  // Function to export single table with images
  const exportTableWithImages = async (tableData, format, fileName, content) => {
    try {
      if (!window.JSZip) {
        throw new Error('JSZip not available');
      }

      const zip = new window.JSZip();
      
      // Add the table data file
      zip.file(fileName, content);

      // Get image for this table
      let imageFileName;
      let imageUrl;

      if (projectData?.type === 'pdf') {
        // For PDF projects, extract the specific page
        const pageNumber = tableData.pageNumber || 1;
        
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pdf/${projectId}/extract-pages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pageNumbers: [pageNumber] })
          });

          if (!response.ok) throw new Error('Failed to extract PDF page');
          
          const data = await response.json();
          if (data.extractedPages && data.extractedPages.length > 0) {
            imageUrl = data.extractedPages[0].downloadUrl;
            imageFileName = fileName.replace(/\.(csv|json)$/, '.png');
          }
        } catch (error) {
          console.error('Error extracting PDF page:', error);
          throw new Error('Failed to extract PDF page for export');
        }
      } else {
        // For snippet projects, get the current image
        const currentFile = projectData?.files?.find(f => 
          f.tables?.some(t => t.id === tableData._id || t.id === selectedTable)
        );
        
        if (currentFile) {
          const imageData = imageUrls.find(img => img.id === currentFile.id);
          if (imageData?.url) {
            imageUrl = imageData.url;
            imageFileName = fileName.replace(/\.(csv|json)$/, '.png');
          }
        }
      }

      // Download and add image to ZIP if available
      if (imageUrl && imageFileName) {
        try {
          const imageResponse = await fetch(imageUrl);
          if (imageResponse.ok) {
            const imageBlob = await imageResponse.blob();
            zip.file(imageFileName, imageBlob);
          }
        } catch (error) {
          console.error('Error adding image to ZIP:', error);
          // Continue without image
        }
      }

      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = fileName.replace(/\.(csv|json)$/, '_with_image.zip');
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      link.href = url;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error creating ZIP with images:', error);
      throw error;
    }
  };

  // Enhanced export all tables function
  const exportAllTables = async () => {
    if (!projectData?.files) {
      console.error('No project data available');
      return;
    }
    
    try {
      setIsExporting(true);
      
      // Get all table IDs and organize by file
      const tablesWithFiles = [];
      projectData.files.forEach((file, fileIndex) => {
        if (file.tables && file.tables.length > 0) {
          file.tables.forEach((table, tableIndex) => {
            tablesWithFiles.push({
              tableId: table.id,
              file: file,
              tableIndex: tableIndex + 1,
              fileIndex: fileIndex
            });
          });
        }
      });
      
      if (tablesWithFiles.length === 0) {
        console.warn('No tables found to export');
        setIsExporting(false);
        return;
      }
      
      console.log(`Preparing to export ${tablesWithFiles.length} tables...`);
      
      // Fetch all tables
      const tablesData = await Promise.all(
        tablesWithFiles.map(async ({ tableId, file, tableIndex, fileIndex }) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${tableId}`);
            if (!response.ok) throw new Error(`Failed to fetch table ${tableId}`);
            const tableData = await response.json();
            return {
              tableData,
              file,
              tableIndex,
              fileIndex
            };
          } catch (error) {
            console.error(`Error fetching table ${tableId}:`, error);
            return null;
          }
        })
      );
      
      const validTables = tablesData.filter(Boolean);
      
      if (validTables.length === 0) {
        console.error('No valid tables to export');
        setIsExporting(false);
        return;
      }

      // Handle images if requested
      let extractedImages = [];
      if (includeOriginalImages) {
        if (projectData.type === 'pdf') {
          // Extract all unique pages that contain tables
          const uniquePages = [...new Set(validTables.map(t => t.tableData.pageNumber))];
          
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pdf/${projectId}/extract-pages`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ pageNumbers: uniquePages })
            });

            if (response.ok) {
              const data = await response.json();
              extractedImages = data.extractedPages || [];
            }
          } catch (error) {
            console.error('Error extracting PDF pages:', error);
          }
        } else {
          // For snippet projects, collect image URLs
          extractedImages = imageUrls.filter(img => img.url).map((img, index) => ({
            pageNumber: index + 1,
            downloadUrl: img.url,
            fileName: `${(projectData.name || 'Project').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}_Page${index + 1}_${projectId}.png`
          }));
        }
      }
      
      // Create files for export
      const projectName = (projectData.name || 'DigiTables_Project').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
      
      // Generate CSV files with standardized naming
      const csvFiles = validTables.map(({ tableData, file, tableIndex, fileIndex }) => {
        let pageNumber;
        if (projectData.type === 'pdf') {
          pageNumber = tableData.pageNumber || 1;
        } else {
          pageNumber = fileIndex + 1; // Use file order for snippet projects
        }
        
        const fileName = `${projectName}_Page${pageNumber}_Table${tableIndex}_${projectId}.csv`;
        
        const csvContent = tableData.currentData
          .map(row => row.map(cell => {
            const cellValue = (cell || '').toString();
            if (cellValue.includes('"') || cellValue.includes(',') || cellValue.includes('\n')) {
              return `"${cellValue.replace(/"/g, '""')}"`;
            }
            return cellValue;
          }).join(","))
          .join("\n");
        
        return { fileName, content: csvContent };
      });
      
      // Handle single file case
      if (csvFiles.length === 1 && !includeOriginalImages) {
        const fileData = csvFiles[0];
        const blob = new Blob([fileData.content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileData.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsExporting(false);
        return;
      }
      
      // Create ZIP for multiple files or when including images
      if (!window.JSZip) {
        alert('JSZip library not available for packaging multiple files.');
        setIsExporting(false);
        return;
      }
      
      const zip = new window.JSZip();
      
      // Add CSV files
      csvFiles.forEach(fileData => {
        zip.file(fileData.fileName, fileData.content);
      });
      
      // Add images if requested
      if (includeOriginalImages && extractedImages.length > 0) {
        for (const image of extractedImages) {
          try {
            const response = await fetch(image.downloadUrl);
            if (response.ok) {
              const blob = await response.blob();
              zip.file(image.fileName, blob);
            }
          } catch (error) {
            console.error(`Error adding image ${image.fileName}:`, error);
          }
        }
      }
      
      // Generate ZIP file
      const suffix = includeOriginalImages ? '_with_images' : '_tables';
      const zipFileName = `${projectName}${suffix}_${new Date().toISOString().slice(0, 10)}.zip`;
      
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting all tables:', error);
      alert(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    // Load the autoHideThumbnailBar setting from localStorage on mount
    const savedSetting = localStorage.getItem('autoHideThumbnailBar');
    if (savedSetting !== null) {
      setAutoHideThumbnailBar(savedSetting === 'true');
    }
  }, []);


  // Cached table data loading with security validation
  const loadTableDataCached = async (tableId) => {
    if (!tableId) {
      console.log('No table ID provided');
      return;
    }

    try {
      // Validate table ID for security
      const validatedTableId = validateTableId(tableId);
    } catch (err) {
      handleApiError(err, 'loadTableDataCached');
      return;
    }

    // Check cache first
    if (tableDataCache.current.has(tableId)) {
      console.log('Loading table data from cache:', tableId);
      const cachedData = tableDataCache.current.get(tableId);
      
      setCurrentTableData(cachedData);
      setTableName(cachedData.caption?.text || `Table ${cachedData.pageNumber}`);
      
      const highlightMap = {};
      cachedData.structure?.highlights?.forEach(h => {
        highlightMap[`${h.row}-${h.col}`] = h.color;
      });
      setHighlights(highlightMap);
      
      if (cachedData.pageNumber) {
        setCurrentPage(cachedData.pageNumber);
      }
      
      return cachedData;
    }

    // Check if already loading to prevent duplicate requests
    if (loadingPromises.current.has(tableId)) {
      console.log('Table data already loading, awaiting existing promise:', tableId);
      return await loadingPromises.current.get(tableId);
    }

    // Create new loading promise
    const loadingPromise = (async () => {
      setIsLoadingTableData(true);
      try {
        console.log('Fetching fresh table data:', tableId);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${tableId}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch table data: ${response.status}`);
        }
        
        const data = await response.json();

        // Security validation: basic data structure check
        if (!data || !data.structure || !Array.isArray(data.currentData)) {
          throw new Error('Invalid table data structure received');
        }

        console.log('Table data loaded:', {
          tableId: tableId,
          rowCount: data.structure.rowCount,
          colCount: data.structure.columnCount,
          hasMetadata: !!data.textractMetadata?.cells
        });

        // Cache the data with timestamp for potential expiration
        tableDataCache.current.set(tableId, {
          ...data,
          _cached: Date.now()
        });

        // Clean up old cache entries if we have too many (memory management)
        if (tableDataCache.current.size > 50) {
          const oldestKey = tableDataCache.current.keys().next().value;
          tableDataCache.current.delete(oldestKey);
        }

        // Update UI state
        setCurrentTableData(data);
        setTableName(data.caption?.text || `Table ${data.pageNumber}`);
        
        const highlightMap = {};
        data.structure?.highlights?.forEach(h => {
          highlightMap[`${h.row}-${h.col}`] = h.color;
        });
        setHighlights(highlightMap);
        
        if (data.pageNumber) {
          setCurrentPage(data.pageNumber);
        }

        return data;
        
      } catch (err) {
        handleApiError(err, 'loadTableDataCached');
        throw err;
      } finally {
        // Delay cleanup to handle rapid re-renders
        setTimeout(() => {
          loadingPromises.current.delete(tableId);
          setIsLoadingTableData(false);
        }, 500); // Increased from immediate cleanup
      }
    })();

    // Store the promise to prevent duplicates
    loadingPromises.current.set(tableId, loadingPromise);
    
    return await loadingPromise;
  };

  useEffect(() => {
    if (isInitialLoad) return; // Skip during initial load

    const fetchTableData = async () => {
      if (!selectedTable) {
        console.log('No table selected');
        return;
      }
  
      setActiveCell({ row: null, col: null });
      await loadTableDataCached(selectedTable);
    };
  
    fetchTableData();
  }, [selectedTable, isInitialLoad]);

  useEffect(() => {
    const user = localStorage.getItem('user');
    setIsAuthenticated(user !== null);
  }, []);

  const handleTableNameChange = async (newName) => {
    if (!selectedTable || !currentTableData || newName === tableName) return;
    
    try {
      console.log('Saving new table name:', newName);
      
      // Update locally
      setTableName(newName);
      setHasUnsavedChanges(true);
      
      // Create a copy of the current table data with the updated caption
      const updatedTableData = {
        ...currentTableData,
        caption: {
          ...(currentTableData.caption || {}),
          text: newName
        }
      };
      
      // Update the table data in state
      setCurrentTableData(updatedTableData);
      
      // Also update the tables array so it reflects in the thumbnail bar
      setTables(prevTables => 
        prevTables.map(table => 
          table.id === selectedTable 
            ? { 
                ...table, 
                caption: { 
                  ...(table.caption || {}), 
                  text: newName 
                } 
              } 
            : table
        )
      );
      
      // Save to server
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${selectedTable}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          caption: {
            ...(currentTableData.caption || {}),
            text: newName
          }
        })
      });
  
      if (!response.ok) {
        console.error('Server responded with error:', response.status);
        throw new Error('Failed to update table name');
      }
      
      const result = await response.json();
      console.log('Table name updated successfully:', result);
      
      // Reset the hasUnsavedChanges flag after successful save
      setHasUnsavedChanges(false);
      
    } catch (error) {
      console.error('Error updating table name:', error);
      // Revert to original name on error
      setTableName(currentTableData?.caption?.text || `Table ${currentTableData?.pageNumber || ''}`);
    }
  };

  const loadJSZip = async () => {
    if (window.JSZip) {
      return window.JSZip;
    }
    
    // Load JSZip from CDN
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.integrity = 'sha512-XMVd28F1oH/O71fzwBnV7HucLxVwtxf26XV8P4wPk26EDxuGZ91N8LsE5hJzumEySMPsWQ0V5C1TIh7GaLHLyg==';
      script.crossOrigin = 'anonymous';
      
      script.onload = () => resolve(window.JSZip);
      script.onerror = () => reject(new Error('Failed to load JSZip library'));
      
      document.head.appendChild(script);
      
      // Set a timeout in case the script doesn't load
      setTimeout(() => reject(new Error('JSZip load timeout')), 8000);
    });
  };

  // post highlight refactor
  const handleQuickDrawUpdate = (row, col, content, newHighlightColor) => {
    if (!currentTableData) return;
    
    // 1. Update cell content if changed
    if (content !== currentTableData.currentData[row][col]) {
      const newData = [...currentTableData.currentData];
      newData[row][col] = content;
      
      setCurrentTableData({
        ...currentTableData,
        currentData: newData
      });
    }
    
    // 2. Update highlight color in highlights object
    if (newHighlightColor) {
      setHighlights(prev => {
        const key = `${row}-${col}`;
        const newHighlights = { ...prev };
        newHighlights[key] = newHighlightColor;
        return newHighlights;
      });
    }
    
    setHasUnsavedChanges(true);
  };

  // post highlight refactor
  const hasQuickDrawEligibleCells = useMemo(() => {
    if (!currentTableData) return false;
    
    // Check for red-highlighted cells with valid bounding box data
    const redHighlights = Object.entries(highlights)
      .filter(([_, color]) => color === '#f44336')
      .map(([key]) => {
        const [row, col] = key.split('-').map(Number);
        return { row, col };
      });
    
    // Check if any red-highlighted cells have valid bounding box data
    return redHighlights.some(({ row, col }) => {
      // Make sure the cell is within the current table boundaries
      if (row >= currentTableData.currentData.length || 
          col >= currentTableData.currentData[0].length) {
        return false;
      }
      // Check for valid bounding box
      return currentTableData.textractMetadata?.cells?.[row]?.[col]?.boundingBox?.Width > 0;
    });
  }, [currentTableData, highlights]);

  // post HIGHLIGHT REFACTOR
  const applyConfidenceThreshold = () => {
    if (!currentTableData) return;
    
    // Create a new highlights object based on current highlights
    const newHighlights = { ...highlights };
    
    // Evaluate all cells against the threshold
    if (currentTableData.textractMetadata?.cells) {
      currentTableData.textractMetadata.cells.forEach((row, rowIdx) => {
        if (!Array.isArray(row)) return;
        
        row.forEach((cell, colIdx) => {
          if (!cell) return;
          
          const key = `${rowIdx}-${colIdx}`;
          const currentHighlight = newHighlights[key];
          
          // Get confidence value
          const confidence = typeof cell.confidence === 'number' ? cell.confidence : 100;
          
          // Check if this is a red highlight (auto-applied)
          const isRedHighlight = currentHighlight === '#f44336';
          
          // If confidence is below threshold and no highlight or it's red, apply/keep red highlight
          if (confidence < tempThreshold) {
            // Only add highlight if none exists
            if (!currentHighlight) {
              newHighlights[key] = '#f44336'; // Apply red highlight
            }
          } 
          // If confidence is above threshold and it's a red highlight, remove it
          else if (isRedHighlight) {
            delete newHighlights[key]; // Remove red highlight
          }
          // Other color highlights are preserved regardless of confidence
        });
      });
    }
    
    // Update the state
    setHighlights(newHighlights);
    setConfidenceThreshold(tempThreshold);
    setHasUnsavedChanges(true);
    setShowThresholdSlider(false);
  };
  
  //function to reset the threshold to its initial value
  const cancelThresholdChange = () => {
    setTempThreshold(confidenceThreshold);
    setShowThresholdSlider(false);
  };

  // Optimized: Load only current file URL first, others lazily
  const loadCurrentFileUrl = async (projectData, targetFile) => {
    if (!targetFile) {
      console.log('No target file provided');
      return;
    }
    
    setIsLoadingFileUrl(true);
    try {
      const targetFileType = targetFile?.fileType;
      
      // Security validation: ensure file belongs to project
      const validatedFileId = validateFileId(targetFile.id);
      if (!projectData.files.some(f => f.id === validatedFileId)) {
        throw new Error('File not found in project');
      }
      
      // Handle PDF files
      if (targetFileType === 'pdf') {
        console.log('Fetching PDF URL for current file:', targetFile.id);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/files/${targetFile.id}/url`);
        
        if (!response.ok) {
          console.error('PDF URL fetch failed:', response.status, response.statusText);
          throw new Error('Failed to fetch PDF URL');
        }
        
        const data = await response.json();
        console.log('PDF URL loaded for current file');
        setPdfUrl(data.url);
      }
      
      // Handle image files - load current image first, initialize others for lazy loading
      const imageFileTypes = ['jpeg', 'jpg', 'png', 'webp', 'tif'];
      const allImageFiles = projectData.files.filter(f => 
        imageFileTypes.includes(f.fileType?.toLowerCase())
      );
      
      if (allImageFiles.length > 0) {
        // Initialize all images with placeholder data for navigation
        const imageUrls = allImageFiles.map(file => ({
          id: file.id,
          url: null, // Will be loaded lazily
          name: file.originalName || file.name,
          sequence: file.sequence || 0,
          status: 'loading', // Change from 'pending' to 'loading' to prevent flash
          tableCount: 0,
          fileType: file.fileType || 'unknown',
          displayType: file.fileType || 'unknown',
          isConverted: false
        })).sort((a, b) => a.sequence - b.sequence);
        
        setImageUrls(imageUrls);
        
        // Set current image index
        const targetIndex = imageUrls.findIndex(img => img.id === targetFile.id);
        if (targetIndex >= 0) {
          setCurrentImageIndex(targetIndex);
        }
        
        // Load only the current image URL immediately - OPTIMIZED SINGLE CALL
        if (targetFileType && imageFileTypes.includes(targetFileType.toLowerCase())) {
          try {
            console.log('Loading current image URL (optimized):', targetFile.id);
            
            // OPTIMIZATION: Single API call instead of two parallel calls
            const urlResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/files/${targetFile.id}/url`);
            
            if (urlResponse.ok) {
              const urlData = await urlResponse.json();
              
              // Update only the current image in the array
              setImageUrls(prev => prev.map(img => 
                img.id === targetFile.id 
                  ? {
                      ...img,
                      url: urlData.url,
                      status: urlData.processingStatus || 'completed', // Now included in URL response
                      tableCount: urlData.tableCount || 0, // Now included in URL response
                      fileType: urlData.originalType || img.fileType,
                      displayType: urlData.displayType || urlData.originalType || img.fileType,
                      isConverted: urlData.isConverted || false
                    }
                  : img
              ));
              
              console.log('Current image URL loaded successfully (optimized)');
            } else {
              console.warn('Failed to load image URL:', urlResponse.status);
            }
          } catch (error) {
            console.error(`Error loading current image ${targetFile.id}:`, error);
          }
        }
        
        // Background prefetch of adjacent images (non-blocking)
        setTimeout(() => {
          prefetchAdjacentImages(allImageFiles, targetIndex);
        }, 100);
      }
    } catch (error) {
      handleApiError(error, 'loadCurrentFileUrl');
    } finally {
      setIsLoadingFileUrl(false);
    }
  };

  // Background prefetch for adjacent images (non-blocking) - OPTIMIZED
  const prefetchAdjacentImages = async (allImageFiles, currentIndex) => {
    const imagesToPrefetch = [];
    
    // Prefetch previous and next images
    if (currentIndex > 0) imagesToPrefetch.push(allImageFiles[currentIndex - 1]);
    if (currentIndex < allImageFiles.length - 1) imagesToPrefetch.push(allImageFiles[currentIndex + 1]);
    
    // Process prefetch requests in parallel for maximum speed
    const prefetchPromises = imagesToPrefetch.map(async (file) => {
      try {
        // OPTIMIZATION: Single API call with caching
        const urlResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/files/${file.id}/url`);
        
        if (urlResponse.ok) {
          const urlData = await urlResponse.json();
          
          setImageUrls(prev => prev.map(img => 
            img.id === file.id 
              ? {
                  ...img,
                  url: urlData.url,
                  status: urlData.processingStatus || 'completed',
                  tableCount: urlData.tableCount || 0,
                  fileType: urlData.originalType || img.fileType,
                  displayType: urlData.displayType || img.fileType,
                  isConverted: urlData.isConverted || false
                }
              : img
          ));
        }
      } catch (error) {
        console.log(`Background prefetch failed for ${file.id}:`, error);
      }
    });
    
    // Execute all prefetch operations in parallel
    await Promise.allSettled(prefetchPromises);
  };

  // Removed duplicate fetchFiles useEffect - now loading URLs directly in main useEffect to prevent double-loading

  // Enhanced file switching with auto-save
  const switchToFile = async (newFileId) => {
    if (newFileId === fileId) return; // Already on this file

    try {
      setIsFileSwitching(true);

      // Auto-save current changes if any
      if (hasUnsavedChanges && selectedTable && currentTableData) {
        console.log('Auto-saving before file switch...');
        const saveSuccess = await saveTableData(true);
        if (!saveSuccess) {
          console.warn('Auto-save failed, but continuing with file switch');
        }
      }

      // Navigate to the new file
      const currentPath = window.location.pathname;
      const newPath = currentPath.replace(/\/file\/[^\/]+/, `/file/${newFileId}`);
      navigate(newPath);

    } catch (error) {
      console.error('Error switching files:', error);
      setError('Failed to switch files');
    } finally {
      setIsFileSwitching(false);
    }
  };

  // Add this function in Workspace.js alongside other data manipulation functions
  const resetTableData = () => {
    if (!currentTableData || !currentTableData.originalData) {
      return;
    }

    // Show a confirmation dialog
    if (!window.confirm('This will reset all your edits, remove ALL highlights, and return to the original OCR data. Continue?')) {
      return;
    }

    try {
      // Create a deep copy of the original data
      const originalData = JSON.parse(JSON.stringify(currentTableData.originalData));
      
      // Reset to original data
      setCurrentTableData({
        ...currentTableData,
        currentData: originalData
      });
      
      // Reset highlights as well, since they're part of the user's edits
      setHighlights({});
      
      // Mark as having unsaved changes
      setHasUnsavedChanges(true);
      
      // Show feedback to user
      console.log('Table reset to original OCR data');
    } catch (error) {
      console.error('Error resetting table data:', error);
      // Show error feedback to user
      alert('An error occurred while resetting the table data.');
    }
  };

  // Add a useEffect to update tables when currentImageIndex changes
  useEffect(() => {
    if (projectData?.type === 'snippet' && projectData?.files && imageUrls.length > 0) {
      const currentImageId = imageUrls[currentImageIndex]?.id;
      if (currentImageId) {
        // Find the file that matches the current image
        const currentFile = projectData.files.find(f => f.id === currentImageId);
        
        if (currentFile?.tables?.length > 0) {
          const sortedTables = [...currentFile.tables].sort((a, b) => {
            // Sort by any logical criteria - for images you might just keep original order
            return 0;
          });
          
          setTables(sortedTables);
          
          // Automatically select the first table of this image
          setSelectedTable(sortedTables[0].id);
        } else {
          // No tables in this image
          setTables([]);
          setSelectedTable(null);
        }
      }
    }
  }, [currentImageIndex, projectData, imageUrls]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isCleaningUp) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
  
    const handleNavigation = (e) => {
      if (hasUnsavedChanges && !isCleaningUp) {
        if (!window.confirm('You have unsaved changes. Do you want to leave without saving?')) {
          e.preventDefault();
        } else {
          setIsCleaningUp(true);
        }
      }
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handleNavigation);
  
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [hasUnsavedChanges, isCleaningUp]);


  const handleHighlight = (rowIndex, colIndex, color) => {
    setHighlights(prev => {
      const key = `${rowIndex}-${colIndex}`;
      const newHighlights = { ...prev };
      
      if (newHighlights[key] === color) {
        delete newHighlights[key];
      } else {
        newHighlights[key] = color;
      }
      
      setHasUnsavedChanges(true);
      return newHighlights;
    });
  };



  // Replace the applyHighlight function with this:
  const applyHighlight = (color) => {
    // Use === null check instead of falsy check to properly handle row 0
    if (!currentTableData || currentSelection?.range?.start?.row === null) return;
    
    const newHighlights = { ...highlights };
    currentSelection.selectedCells.forEach(({ row, col }) => {
      const key = `${row}-${col}`;
      newHighlights[key] = color;
    });

    setHighlights(newHighlights);
    setHasUnsavedChanges(true);
  };

  // post HIGHLIGHT REFACTOR
  const removeHighlights = () => {
    if (!currentTableData || currentSelection?.range?.start?.row === null) return;
    
    console.log('Removing highlights from selected cells');
    
    // Simply remove user-applied highlights from the highlights object
    const newHighlights = { ...highlights };
    currentSelection.selectedCells.forEach(({ row, col }) => {
      const key = `${row}-${col}`;
      delete newHighlights[key];
    });
  
    setHighlights(newHighlights);
    setHasUnsavedChanges(true);
  };

  const handleBackNavigation = () => {
    if (!isAuthenticated) {
      // For unauthenticated/shared access, go to home page
      navigate('/');
      return;
    }
    
    const performNavigation = () => {
      const navigationState = location.state;
      
      if (navigationState) {
        // Navigate back with cached state to avoid API calls
        navigate('/dashboard', {
          state: {
            restoreNavigation: true,
            selectedProject: navigationState.project,
            selectedCollection: navigationState.collection || currentCollection,
            currentView: navigationState.currentView || (navigationState.collection || currentCollection ? 'files' : 'collections'),
            refreshData: true // Signal to refresh data after workspace changes
          }
        });
      } else {
        // Create navigation context based on current file's collection
        const navState = {
          restoreNavigation: true,
          selectedProject: projectData ? { id: projectData.id, name: projectData.name } : null,
          selectedCollection: currentCollection,
          currentView: currentCollection ? 'files' : 'collections',
          refreshData: true
        };
        navigate('/dashboard', { state: navState });
      }
    };
    
    // Regular authenticated flow with unsaved changes check
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to leave without saving?')) {
        setIsCleaningUp(true);
        performNavigation();
      }
    } else {
      performNavigation();
    }
  };


  const handleTableSelect = (tableId) => {
    if (isInitialLoad) return; // Prevent selection during initial load
    setActiveCell({ row: null, col: null });
    setSelectedTable(tableId);
    
    const selectedTable = tables.find(t => t.id === tableId);
    
    if (selectedTable) {
      if (projectData?.type === 'pdf') {
        // For PDF projects, we jump to the page
        jumpToPage(selectedTable.pageNumber - 1);
      } else {
        // For snippet projects, we need to find the file that contains this table
        const fileWithTable = projectData?.files?.find(file => 
          file.tables?.some(table => table.id === tableId)
        );
        
        if (fileWithTable) {
          const fileIndex = imageUrls.findIndex(img => img.id === fileWithTable.id);
          if (fileIndex >= 0) {
            setCurrentImageIndex(fileIndex);
          }
        }
      }
    }
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    if (!currentTableData) return;
  
    const newData = [...currentTableData.currentData];
    newData[rowIndex][colIndex] = value;
    setCurrentTableData({
      ...currentTableData,
      currentData: newData
    });
    setHasUnsavedChanges(true);
    setFormulaBarValue(value);
  };

  const handleCellClick = (rowIndex, colIndex) => {
    setActiveCell({ row: rowIndex, col: colIndex });
  };

  const saveTableData = useCallback(async (isAutoSave = false) => {
    // Only proceed if we have valid data and no save in progress
    if (!selectedTable || !currentTableData) {
      console.log('Save skipped - invalid data');
      return false;
    }
    
    // Check if save is already in progress
    if (saveInProgressRef.current) {
      console.log('Save already in progress, skipping');
      return false;
    }
    
    // console.log(`Starting ${isAutoSave ? 'auto' : 'manual'} save operation`);
    
    // Set flag to prevent concurrent saves
    saveInProgressRef.current = true;
    
    try {
      if (isAutoSave) {
        setIsAutosaving(true);
      }
  
      // Create highlights array
      const highlightArray = Object.entries(highlights).map(([key, color]) => {
        const [row, col] = key.split('-');
        return {
          row: parseInt(row),
          col: parseInt(col),
          color
        };
      });
  
      // Prepare the payload
      const payload = {
        currentData: currentTableData.currentData,
        structure: {
          ...currentTableData.structure,
          highlights: highlightArray
        },
        textractMetadata: currentTableData.textractMetadata 
      };

      // Add debug logging
      console.log('=== SAVE DEBUG ===');
      console.log('Saving table with metadata:', {
        rowCount: currentTableData.structure.rowCount,
        colCount: currentTableData.structure.columnCount,
        metadataRowCount: currentTableData.textractMetadata?.cells?.length,
        metadataColCount: currentTableData.textractMetadata?.cells?.[0]?.length,
        sampleMetadata: currentTableData.textractMetadata?.cells?.slice(0, 2).map(row => 
          row?.slice(0, 2).map(cell => ({
            hasBox: !!cell?.boundingBox,
            boxWidth: cell?.boundingBox?.Width || 'null'
          }))
        )
      });
      
      // Include caption if it exists
      if (tableName) {
        payload.caption = {
          ...(currentTableData.caption || {}),
          text: tableName
        };
      }
  
      // Log the actual save operation for debugging
      console.log(`Saving table ${selectedTable}...`);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${selectedTable}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
  
      if (!response.ok) {
        throw new Error(`Failed to save changes - status ${response.status}`);
      }
  
      // Update cache with saved data
      if (tableDataCache.current.has(selectedTable)) {
        const cachedData = tableDataCache.current.get(selectedTable);
        tableDataCache.current.set(selectedTable, {
          ...cachedData,
          currentData: currentTableData.currentData,
          structure: {
            ...currentTableData.structure,
            highlights: highlightArray
          },
          caption: payload.caption,
          _cached: Date.now()
        });
      }

      // Update state after successful save - IMPORTANT: wrap in a timeout to avoid state update conflicts
      setTimeout(() => {
        setHasUnsavedChanges(false);
        setLastSaved(new Date());
      }, 0);
      
      // console.log(`${isAutoSave ? 'Auto' : 'Manual'} save completed successfully`);
      return true;
    } catch (error) {
      console.error(`Error ${isAutoSave ? 'auto-saving' : 'saving'} table:`, error);
      return false;
    } finally {
      // IMPORTANT: Delay resetting the saveInProgressRef to avoid race conditions
      setTimeout(() => {
        saveInProgressRef.current = false;
        if (isAutoSave) {
          setIsAutosaving(false);
        }
      }, 50);
    }
  }, [selectedTable, currentTableData, tableName, highlights]);

  const handleSave = async () => {
    console.log("Manual save triggered");
    
    // Disable the button while saving (additional protection)
    const savingButtonRef = document.getElementById('manual-save-button');
    if (savingButtonRef) {
      savingButtonRef.disabled = true;
    }
    
    try {
      await saveTableData(false);
    } finally {
      // Re-enable the button after a delay
      setTimeout(() => {
        if (savingButtonRef) {
          savingButtonRef.disabled = false;
        }
      }, 500);
    }
  };

  useEffect(() => {
    // Load the includeBlankCells setting from localStorage on mount
    const savedSetting = localStorage.getItem('includeBlankCells');
    if (savedSetting !== null) {
      setIncludeBlankCells(savedSetting === 'true');
    }
  }, []);
  
  // Add function to update the setting
  const updateIncludeBlankCells = (value) => {
    setIncludeBlankCells(value);
    localStorage.setItem('includeBlankCells', value);
  };

  // Add this function alongside updateIncludeBlankCells
  const updateAutoHideThumbnailBar = (value) => {
    setAutoHideThumbnailBar(value);
    localStorage.setItem('autoHideThumbnailBar', value);
  };

  // Add this effect to handle clicks outside thumbnail bar
  useEffect(() => {
    if (!autoHideThumbnailBar) return;
    
    const handleClickOutside = (e) => {
      // Get the thumbnail container element
      const thumbnailBar = document.querySelector('.thumbnail-bar');
      if (!thumbnailBar) return;
      
      // Check if the click was outside the thumbnail bar
      if (!thumbnailBar.contains(e.target) && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autoHideThumbnailBar, sidebarOpen]);

  const performAutosave = useCallback(() => {
    // console.log("Autosave check triggered");
    
    if (hasUnsavedChanges && !saveInProgressRef.current) {
      // console.log("Unsaved changes detected, performing autosave");
      saveTableData(true).then(success => {
        // console.log(`Autosave completed with status: ${success ? 'success' : 'failed'}`);
      });
    } else {
      // console.log("No autosave needed - no changes or save in progress");
    }
  }, [hasUnsavedChanges, saveTableData]);

  useEffect(() => {
    // console.log("Setting up autosave interval");
    
    // Set up an interval that runs every minute
    const intervalId = setInterval(() => {
      // console.log("Autosave interval triggered");
      performAutosave();
    }, 5000); // 5 seconds
    
    // Clean up interval on component unmount
    return () => {
      // console.log("Cleaning up autosave interval");
      clearInterval(intervalId);
    };
  }, [performAutosave]); // Only depend on performAutosave

  useEffect(() => {
    return () => {
      if (hasUnsavedChanges && !saveInProgressRef.current && !isCleaningUp) {
        console.log("Component unmounting with unsaved changes, triggering final save");
        saveTableData(true);
      }
    };
  }, []); // Empty dependency array



  

  const [jsZipLoaded, setJsZipLoaded] = useState(false);

  // Add this effect to load JSZip once when the component mounts
  useEffect(() => {
    // Check if JSZip is already loaded
    if (window.JSZip) {
      setJsZipLoaded(true);
      return;
    }
    
    // Load JSZip from CDN
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    script.async = true;
    
    script.onload = () => {
      console.log('JSZip loaded successfully');
      setJsZipLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load JSZip library');
    };
    
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const mergeCells = () => {
    if (!currentTableData || !currentSelection?.selectedCells || currentSelection.selectedCells.length <= 1) {
      return;
    }
  
    // Make a deep copy of the current data
    const newData = [...currentTableData.currentData.map(row => [...row])];
    
    // Group selected cells by row
    const cellsByRow = {};
    currentSelection.selectedCells.forEach(({ row, col }) => {
      if (!cellsByRow[row]) {
        cellsByRow[row] = [];
      }
      cellsByRow[row].push({ col, value: newData[row][col] || '' });
    });
    
    // Process each row
    Object.entries(cellsByRow).forEach(([rowIndex, cells]) => {
      rowIndex = parseInt(rowIndex);
      
      // Sort cells by column to maintain the original order
      cells.sort((a, b) => a.col - b.col);
      
      // Get the leftmost column in this row
      const leftmostCol = Math.min(...cells.map(cell => cell.col));
      
      // Concatenate all cell values in this row (with space separator)
      const concatenatedValue = cells
        .map(cell => cell.value)
        .filter(Boolean) // Remove empty values
        .join(' ');
      
      // Set the leftmost cell value to the concatenated value
      newData[rowIndex][leftmostCol] = concatenatedValue;
      
      // Clear all other cells in this row's selection
      cells.forEach(cell => {
        if (cell.col !== leftmostCol) {
          newData[rowIndex][cell.col] = '';
        }
      });
    });
    
    // Update the table data
    setCurrentTableData({
      ...currentTableData,
      currentData: newData
    });
    
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };


  // Add this effect to handle closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fileDropdownRef.current && !fileDropdownRef.current.contains(e.target)) {
        setShowFileDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Removed handleFileSelect - using switchToFile for better auto-save integration

  const addRow = (position) => {
    if (!currentTableData || selectedCell.row === null) return;
    
    const newData = [...currentTableData.currentData];
    const newRow = new Array(currentTableData.structure.columnCount).fill('');
    
    // Position determines whether to insert above or below the selected row
    const insertIndex = position === 'above' ? selectedCell.row : selectedCell.row + 1;
    
    // Insert at the determined position
    newData.splice(insertIndex, 0, newRow);
    
    // Create a new metadata structure with the added row
    const newTextractMetadata = {
      ...currentTableData.textractMetadata,
      cells: [...currentTableData.textractMetadata.cells] // Create a shallow copy of the cells array
    };
    
    // If cells exist in metadata, update them
    if (newTextractMetadata.cells && Array.isArray(newTextractMetadata.cells)) {
      // Create an empty metadata row with the same number of columns
      const emptyMetadataRow = Array(currentTableData.structure.columnCount).fill().map((_, colIndex) => ({
        confidence: 100,
        boundingBox: null,  // Important: new cells have no bounding box
        content: '',
        rowIndex: insertIndex,
        columnIndex: colIndex,
        isHeader: false,
        relationships: []
      }));
      
      // IMPORTANT: Create a new array to avoid mutation issues
      const newCells = [];
      
      // Copy cells before the insert point
      for (let i = 0; i < insertIndex; i++) {
        if (newTextractMetadata.cells[i]) {
          newCells.push([...newTextractMetadata.cells[i]]);
        }
      }
      
      // Insert the new empty row
      newCells.push(emptyMetadataRow);
      
      // Copy cells after the insert point and update their indices
      for (let i = insertIndex; i < newTextractMetadata.cells.length; i++) {
        if (newTextractMetadata.cells[i]) {
          const shiftedRow = newTextractMetadata.cells[i].map(cell => {
            if (cell) {
              return {
                ...cell,
                rowIndex: i + 1  // Shift row index down by 1
              };
            }
            return cell;
          });
          newCells.push(shiftedRow);
        }
      }
      
      newTextractMetadata.cells = newCells;
    }
    
    // Update highlights to shift with the content
    const newHighlights = {};
    Object.entries(highlights).forEach(([key, color]) => {
      const [row, col] = key.split('-').map(Number);
      if (row >= insertIndex) {
        // Shift highlights down
        newHighlights[`${row + 1}-${col}`] = color;
      } else {
        // Keep highlights in place
        newHighlights[key] = color;
      }
    });
    
    setHighlights(newHighlights);
    
    setCurrentTableData({
      ...currentTableData,
      currentData: newData,
      textractMetadata: newTextractMetadata,
      structure: {
        ...currentTableData.structure,
        rowCount: newData.length
      }
    });

    console.log('=== POST-MODIFICATION VALIDATION ===');
    console.log('After modification:', {
      dataRows: newData.length,
      dataCols: newData[0]?.length,
      metadataRows: newTextractMetadata.cells?.length,
      metadataCols: newTextractMetadata.cells?.[0]?.length,
      allRowsHaveSameColCount: newTextractMetadata.cells?.every(row => 
        row?.length === newTextractMetadata.cells[0]?.length
      ),
      nullBoundingBoxes: newTextractMetadata.cells?.flat().filter(cell => 
        cell && cell.boundingBox === null
      ).length
    });
    
    setHasUnsavedChanges(true);
  };

  const deleteCurrentRow = () => {
    if (!currentTableData || selectedCell.row === null) return;
    
    const newData = currentTableData.currentData.filter((_, index) => index !== selectedCell.row);
    
    // Create a new metadata structure without the deleted row
    const newTextractMetadata = {
      ...currentTableData.textractMetadata,
      cells: [...currentTableData.textractMetadata.cells]
    };
    
    // If cells exist in metadata, update them
    if (newTextractMetadata.cells && Array.isArray(newTextractMetadata.cells)) {
      // Create a new array excluding the deleted row
      const newCells = [];
      
      // Copy cells before the deleted row
      for (let i = 0; i < selectedCell.row; i++) {
        if (newTextractMetadata.cells[i]) {
          newCells.push([...newTextractMetadata.cells[i]]);
        }
      }
      
      // Copy cells after the deleted row and update their indices
      for (let i = selectedCell.row + 1; i < newTextractMetadata.cells.length; i++) {
        if (newTextractMetadata.cells[i]) {
          const shiftedRow = newTextractMetadata.cells[i].map(cell => {
            if (cell) {
              return {
                ...cell,
                rowIndex: i - 1  // Shift row index up by 1
              };
            }
            return cell;
          });
          newCells.push(shiftedRow);
        }
      }
      
      newTextractMetadata.cells = newCells;
    }
    
    // Update highlights to shift with the content
    const newHighlights = {};
    Object.entries(highlights).forEach(([key, color]) => {
      const [row, col] = key.split('-').map(Number);
      if (row < selectedCell.row) {
        // Keep highlights above deleted row in place
        newHighlights[key] = color;
      } else if (row > selectedCell.row) {
        // Shift highlights up
        newHighlights[`${row - 1}-${col}`] = color;
      }
      // Highlights on the deleted row are removed
    });
    
    setHighlights(newHighlights);
    
    setCurrentTableData({
      ...currentTableData,
      currentData: newData,
      textractMetadata: newTextractMetadata,
      structure: {
        ...currentTableData.structure,
        rowCount: currentTableData.structure.rowCount - 1
      }

      
    });

    console.log('=== POST-MODIFICATION VALIDATION ===');
    console.log('After modification:', {
      dataRows: newData.length,
      dataCols: newData[0]?.length,
      metadataRows: newTextractMetadata.cells?.length,
      metadataCols: newTextractMetadata.cells?.[0]?.length,
      allRowsHaveSameColCount: newTextractMetadata.cells?.every(row => 
        row?.length === newTextractMetadata.cells[0]?.length
      ),
      nullBoundingBoxes: newTextractMetadata.cells?.flat().filter(cell => 
        cell && cell.boundingBox === null
      ).length
    });
    
    // Reset selection if we deleted the selected row
    setSelectedCell(prev => ({
      row: prev.row >= newData.length ? newData.length - 1 : prev.row,
      col: prev.col
    }));
    
    setHasUnsavedChanges(true);
  };

  const addColumn = (position) => {
    if (!currentTableData || selectedCell.col === null) return;
    
    const insertIndex = position === 'left' ? selectedCell.col : selectedCell.col + 1;
    
    const newData = currentTableData.currentData.map(row => {
      const newRow = [...row];
      newRow.splice(insertIndex, 0, '');
      return newRow;
    });
    
    // Create a new metadata structure with the added column
    const newTextractMetadata = {
      ...currentTableData.textractMetadata,
      cells: [...currentTableData.textractMetadata.cells]
    };
    
    // If cells exist in metadata, update them
    if (newTextractMetadata.cells && Array.isArray(newTextractMetadata.cells)) {
      // Process each row
      newTextractMetadata.cells = newTextractMetadata.cells.map((row, rowIndex) => {
        if (!Array.isArray(row)) return row;
        
        const newRow = [];
        
        // Copy cells before the insert point
        for (let i = 0; i < insertIndex; i++) {
          if (row[i]) {
            newRow.push({...row[i]});
          } else {
            newRow.push(null);
          }
        }
        
        // Insert empty cell
        newRow.push({
          confidence: 100,
          boundingBox: null,  // Important: new cells have no bounding box
          content: '',
          rowIndex: rowIndex,
          columnIndex: insertIndex,
          isHeader: rowIndex === 0,
          relationships: []
        });
        
        // Copy cells after the insert point and update their column indices
        for (let i = insertIndex; i < row.length; i++) {
          if (row[i]) {
            newRow.push({
              ...row[i],
              columnIndex: i + 1  // Shift column index right by 1
            });
          } else {
            newRow.push(null);
          }
        }
        
        return newRow;
      });
    }
    
    // Update highlights to shift with the content
    const newHighlights = {};
    Object.entries(highlights).forEach(([key, color]) => {
      const [row, col] = key.split('-').map(Number);
      if (col >= insertIndex) {
        // Shift highlights right
        newHighlights[`${row}-${col + 1}`] = color;
      } else {
        // Keep highlights in place
        newHighlights[key] = color;
      }
    });
    
    setHighlights(newHighlights);
    
    setCurrentTableData({
      ...currentTableData,
      currentData: newData,
      textractMetadata: newTextractMetadata,
      structure: {
        ...currentTableData.structure,
        columnCount: currentTableData.structure.columnCount + 1
      }
    });
    
    setHasUnsavedChanges(true);
  };

  const deleteCurrentColumn = () => {
    if (!currentTableData || selectedCell.col === null) return;
    
    const newData = currentTableData.currentData.map(row => {
      const newRow = [...row];
      newRow.splice(selectedCell.col, 1);
      return newRow;
    });
    
    // Create a new metadata structure without the deleted column
    const newTextractMetadata = {
      ...currentTableData.textractMetadata,
      cells: [...currentTableData.textractMetadata.cells]
    };
    
    // If cells exist in metadata, update them
    if (newTextractMetadata.cells && Array.isArray(newTextractMetadata.cells)) {
      // Process each row
      newTextractMetadata.cells = newTextractMetadata.cells.map((row, rowIndex) => {
        if (!Array.isArray(row)) return row;
        
        const newRow = [];
        
        // Copy cells before the deleted column
        for (let i = 0; i < selectedCell.col; i++) {
          if (row[i]) {
            newRow.push({...row[i]});
          } else {
            newRow.push(null);
          }
        }
        
        // Skip the deleted column and copy cells after it with updated indices
        for (let i = selectedCell.col + 1; i < row.length; i++) {
          if (row[i]) {
            newRow.push({
              ...row[i],
              columnIndex: i - 1  // Shift column index left by 1
            });
          } else {
            newRow.push(null);
          }
        }
        
        return newRow;
      });
    }
    
    // Update highlights to shift with the content
    const newHighlights = {};
    Object.entries(highlights).forEach(([key, color]) => {
      const [row, col] = key.split('-').map(Number);
      if (col < selectedCell.col) {
        // Keep highlights to the left of deleted column in place
        newHighlights[key] = color;
      } else if (col > selectedCell.col) {
        // Shift highlights left
        newHighlights[`${row}-${col - 1}`] = color;
      }
      // Highlights on the deleted column are removed
    });
    
    setHighlights(newHighlights);
    
    setCurrentTableData({
      ...currentTableData,
      currentData: newData,
      textractMetadata: newTextractMetadata,
      structure: {
        ...currentTableData.structure,
        columnCount: currentTableData.structure.columnCount - 1
      }
    });
    
    // Reset selection if we deleted the selected column
    setSelectedCell(prev => ({
      row: prev.row,
      col: prev.col >= newData[0].length ? newData[0].length - 1 : prev.col
    }));
    
    setHasUnsavedChanges(true);
  };

  const handleCellSelection = useCallback((row, col, selectionRange, selectedCells) => {
    // console.log("handleCellSelection called:", { row, col, selectionRange, selectedCellsCount: selectedCells?.length });
    
    setSelectedCell({ row, col });
    setCurrentSelection({
      range: selectionRange,
      selectedCells: selectedCells
    });
    
    if (currentTableData?.currentData?.[row]?.[col] !== undefined) {
      setFormulaBarValue(currentTableData.currentData[row][col] || '');
    }
  }, [currentTableData]);

  const handleImageChange = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  // Enhanced single table export
  const downloadCSV = async (tableData, fileName = null) => {
    if (!tableData || !tableData.currentData) return;

    const { currentData } = tableData;
    
    // Generate standardized filename if not provided
    const finalFileName = fileName || generateStandardFileName(tableData, 1, 'csv');
    
    // Create CSV content
    const csvContent = currentData
      .map(row => row.map(cell => {
        const cellValue = (cell || '').toString();
        if (cellValue.includes('"') || cellValue.includes(',') || cellValue.includes('\n')) {
          return `"${cellValue.replace(/"/g, '""')}"`;
        }
        return cellValue;
      }).join(","))
      .join("\n");

    // If includeOriginalImages is enabled, handle with images
    if (includeOriginalImages) {
      try {
        await exportTableWithImages(tableData, 'csv', finalFileName, csvContent);
      } catch (error) {
        console.error('Error exporting with images, falling back to CSV only:', error);
        // Fallback to regular CSV download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", finalFileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } else {
      // Regular CSV download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", finalFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };
  
  // Toggle viewer collapse function
  const toggleViewerCollapse = () => {
    setViewerCollapsed(!viewerCollapsed);
  };

  // Enhanced JSON export
  const downloadJSON = async (tableData, customFileName = null) => {
    if (!tableData) return;

    const jsonContent = JSON.stringify(tableData, null, 2);
    const finalFileName = customFileName || generateStandardFileName(tableData, 1, 'json');

    if (includeOriginalImages) {
      try {
        await exportTableWithImages(tableData, 'json', finalFileName, jsonContent);
      } catch (error) {
        console.error('Error exporting with images, falling back to JSON only:', error);
        // Fallback to regular JSON download
        const blob = new Blob([jsonContent], { type: "application/json;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", finalFileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } else {
      // Regular JSON download
      const blob = new Blob([jsonContent], { type: "application/json;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", finalFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  

  const exportAllTablesAsJSON = async () => {
    if (!projectData?.files) {
      console.error('No project data available');
      return;
    }
    
    try {
      setIsExporting(true);
      
      // Get all table IDs from all files in the project
      const allTableIds = [];
      projectData.files.forEach(file => {
        if (file.tables && file.tables.length > 0) {
          file.tables.forEach((table, tableIndex) => {
            allTableIds.push({
              tableId: table.id,
              fileName: file.name,
              tableIndex: tableIndex + 1
            });
          });
        }
      });
      
      if (allTableIds.length === 0) {
        console.warn('No tables found to export');
        setIsExporting(false);
        return;
      }
      
      console.log(`Preparing to export ${allTableIds.length} tables as JSON...`);
      
      // Fetch all tables
      const tablesData = await Promise.all(
        allTableIds.map(async ({ tableId, fileName, tableIndex }) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tables/${tableId}`);
            if (!response.ok) throw new Error(`Failed to fetch table ${tableId}`);
            const tableData = await response.json();
            return {
              tableData,
              fileName,
              tableIndex
            };
          } catch (error) {
            console.error(`Error fetching table ${tableId}:`, error);
            return null; // Skip failed tables
          }
        })
      );
      
      // Filter out failed tables
      const validTables = tablesData.filter(Boolean);
      console.log(`Successfully fetched ${validTables.length}/${allTableIds.length} tables`);
      
      if (validTables.length === 0) {
        console.error('No valid tables to export');
        setIsExporting(false);
        return;
      }
      
      // For a single table, just download it directly
      if (validTables.length === 1) {
        const { tableData, fileName, tableIndex } = validTables[0];
        
        // Get project name
        const projectName = (projectData?.name || 'Project')
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '_');
          
        // Format file name
        const cleanFileName = fileName
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '_');
          
        const finalFileName = `${projectName}_${cleanFileName}_Table${tableIndex}.json`;
        
        downloadJSON(tableData, finalFileName);
        setIsExporting(false);
        return;
      }
      
      // For multiple tables, check if JSZip is available
      if (!window.JSZip && !jsZipLoaded) {
        alert('JSZip library not available. Files will be downloaded individually.');
        // Fall back to sequential downloads
        validTables.forEach(({ tableData, fileName, tableIndex }, index) => {
          setTimeout(() => {
            // Get project name
            const projectName = (projectData?.name || 'Project')
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '_');
              
            // Format file name
            const cleanFileName = fileName
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '_');
              
            const finalFileName = `${projectName}_${cleanFileName}_Table${tableIndex}.json`;
            
            downloadJSON(tableData, finalFileName);
          }, index * 500);
        });
        setIsExporting(false);
        return;
      }
      
      // Create a zip file using JSZip
      const zip = new window.JSZip();
      
      // Get project name once
      const projectName = (projectData?.name || 'Project')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_');
      
      // Add all tables to the zip
      validTables.forEach(({ tableData, fileName, tableIndex }) => {
        // Format file name
        const cleanFileName = fileName
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '_');
          
        const finalFileName = `${projectName}_${cleanFileName}_Table${tableIndex}.json`;
        
        // Add the JSON file to the zip
        zip.file(finalFileName, JSON.stringify(tableData, null, 2));
      });
      
      // Project name for the ZIP file
      const zipFileName = `${projectName}_Tables_JSON_${new Date().toISOString().slice(0, 10)}.zip`;
      
      // Generate the zip file
      console.log('Generating ZIP file...');
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6 // Balanced compression
        }
      });
      
      // Download the zip file
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('ZIP file generated and downloaded successfully');
      
    } catch (error) {
      console.error('Error exporting all tables as JSON:', error);
      alert(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const renderContentViewer = () => {
    if (viewerCollapsed) {
      return (
        <div className={`flex h-full w-full items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          {/* Display collapsed state with button to expand */}
          <div className="flex flex-col items-center">
            <button
              onClick={toggleViewerCollapse}
              className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} 
                shadow-md transition-colors mb-3`}
              title="Expand viewer"
            >
              <Maximize size={24} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
            </button>
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Viewer collapsed
            </span>
          </div>
        </div>
      );
    }

    // Determine viewer type based on current file, not entire project
    const currentFile = fileId 
      ? projectData?.files?.find(f => f.id === fileId)
      : projectData?.files?.[0];
    
    const currentFileType = currentFile?.fileType;
    const isImageFile = currentFileType && ['jpeg', 'jpg', 'png', 'webp', 'tif'].includes(currentFileType.toLowerCase());
    const isPDFFile = currentFileType === 'pdf';
    
    console.log('Rendering content viewer:', {
      fileId,
      currentFile: currentFile?.id,
      currentFileType,
      isImageFile,
      isPDFFile,
      availableFiles: projectData?.files?.map(f => ({ id: f.id, type: f.fileType }))
    });

    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      );
    }
  
    if (error) {
      return (
        <div className="flex items-center justify-center h-full text-red-500">
          {error}
        </div>
      );
    }

    // Render PDF Viewer for PDF files
    if (isPDFFile) {
      console.log('Rendering PDF viewer for file:', currentFile?.id);
      return (
        <PDFViewer
          loading={loading}
          error={error}
          pdfUrl={pdfUrl}
          isDarkMode={isDarkMode}
          currentTableData={currentTableData}
          currentSelection={currentSelection}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          defaultLayoutPluginInstance={defaultLayoutPluginInstance}
          pageNavigationPluginInstance={pageNavigationPluginInstance}
        />
      );
    }
    
    // Render Image Viewer for image files
    else if (isImageFile) {
      console.log('Rendering image viewer for file:', currentFile?.id);
      return (
        <ImageViewer
          loading={loading}
          error={error}
          imageUrl={imageUrls[currentImageIndex]?.url}
          isDarkMode={isDarkMode}
          currentTableData={currentTableData}
          currentSelection={currentSelection}
          images={imageUrls}
          currentImageIndex={currentImageIndex}
          onImageChange={handleImageChange}
        />
      );
    }
    
    // Fallback for unsupported file types or missing files
    return (
      <div className={`flex items-center justify-center h-full ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="text-center">
          <div className="mb-2">Unsupported file type: {currentFileType || 'Unknown'}</div>
          <div className="text-sm">Supported types: PDF, JPEG, PNG, WEBP, TIF</div>
        </div>
      </div>
    );
  };

  const renderGrid = () => {
    if (!currentTableData) return null;
  
    const { currentData, structure } = currentTableData;
    const columns = Array.from(
      { length: structure.columnCount }, 
      (_, i) => String.fromCharCode(65 + i)
    );
  
    return (
      <div className="relative">
        <div className="flex">
          {/* Enhanced Table Editor */}
          <div className="flex-1">
            <TableEditor
              data={currentData}
              onChange={(newData) => {
                setCurrentTableData({
                  ...currentTableData,
                  currentData: newData
                });
                setHasUnsavedChanges(true);
              }}
              isDarkMode={isDarkMode}
              highlights={highlights}
              onHighlight={handleHighlight}
              textractMetadata={{
                ...currentTableData.textractMetadata,
                manuallyRemovedHighlights: currentTableData.manuallyRemovedHighlights,
                confidenceThreshold: confidenceThreshold
              }}
              onCellSelect={handleCellSelection}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? 'dark bg-gray-900' : 'bg-white'} pb-12`}>
      <header className={`border-b ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-white'}`}>
        <div className="flex items-center h-14 px-4 gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-shrink-0">
            <button 
              onClick={handleBackNavigation}
              className={`hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded-lg transition-colors ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className={`text-lg font-semibold bg-gradient-to-r ${isDarkMode ? 'from-yellow-300 via-green-400 to-blue-500':'from-pink-500 via-blue-500 to-purple-500'} bg-clip-text text-transparent`}>
              DigiTables
            </div>
          </div>

          {/* New Hierarchical Breadcrumb Navigation */}
          <div className="flex items-center gap-1 text-sm overflow-hidden flex-grow min-w-0">
            {/* Project */}
            <div className={`truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} font-medium`}
                title={projectData?.name || ""}>
              {projectData?.name || "Loading..."}
            </div>

            {/* Separator */}
            <div className={`mx-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <ChevronRight size={16} />
            </div>

            {/* Collection (if file is in a collection) */}
            {currentCollection ? (
              <>
                <div className={`truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} font-medium`}
                    title={currentCollection.name}>
                  {currentCollection.name}
                </div>

                {/* Separator */}
                <div className={`mx-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <ChevronRight size={16} />
                </div>
              </>
            ) : (
              <>
                <div className={`truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} font-medium opacity-60`}>
                  Root Files
                </div>

                {/* Separator */}
                <div className={`mx-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <ChevronRight size={16} />
                </div>
              </>
            )}

            {/* File - with dropdown for collection files */}
            <div className="relative" ref={fileDropdownRef}>
              <div
                onClick={() => {
                  console.log('File dropdown clicked', { collectionFilesLength: collectionFiles.length, showFileDropdown });
                  if (collectionFiles.length > 1) {
                    setShowFileDropdown(!showFileDropdown);
                  }
                }}
                className={`truncate max-w-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} font-medium
                  ${collectionFiles.length > 1 ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''}
                  px-2 py-1 rounded flex items-center`}
                title={collectionFiles.length > 1 ? "Select file from collection" : "Current file"}
              >
                {isFileSwitching && (
                  <div className="animate-spin h-3 w-3 border-t-2 border-b-2 border-blue-500 rounded-full mr-2"></div>
                )}
                <span className="truncate">
                  {projectData?.files?.[0]?.name || "Loading..."}
                </span>
                {collectionFiles.length > 1 && (
                  <ChevronDown className={`ml-1 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                )}
              </div>

              {/* Collection Files dropdown with scrollable area */}
              {showFileDropdown && collectionFiles.length > 1 && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    Files in {currentCollection?.name || 'Collection'} ({collectionFiles.length})
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {collectionFiles.map(file => (
                      <div
                        key={file.id}
                        onClick={() => {
                          console.log('Switching to file:', file.id);
                          switchToFile(file.id);
                          setShowFileDropdown(false);
                        }}
                        className={`px-4 py-3 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-50 dark:border-gray-700 last:border-b-0 ${
                          file.id === fileId
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'text-gray-800 dark:text-gray-200'
                        } ${isFileSwitching ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            {file.processingStatus === 'processing' && (
                              <div className="animate-spin h-3 w-3 border-t-2 border-b-2 border-blue-500 rounded-full mr-2 flex-shrink-0"></div>
                            )}
                            <span className="truncate font-medium">{file.name}</span>
                          </div>
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 uppercase flex-shrink-0">
                            {file.fileType}
                          </span>
                        </div>
                        {file.processingStatus && file.processingStatus !== 'completed' && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Status: {file.processingStatus}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            {/* <button 
              onClick={toggleTheme}
              className={`px-3 py-1.5 text-sm ${
                isDarkMode 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-600 hover:bg-gray-50'
              } rounded-lg transition-colors flex items-center gap-2`}>
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button> */}
            
            {/* Autosave indicator */}
            {isAutosaving && (
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                <div className="animate-spin h-3 w-3 border-t-2 border-b-2 border-blue-500 rounded-full mr-1"></div>
                Autosaving...
              </span>
            )}
            {lastSaved && !isAutosaving && (
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}

            <button 
              id="manual-save-button"
              onClick={handleSave}
              className={`px-3 py-1.5 text-sm ${
                hasUnsavedChanges 
                  ? isDarkMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-black text-white hover:bg-gray-900'
                  : isDarkMode
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              } rounded-lg transition-colors flex items-center gap-2`}
              disabled={!hasUnsavedChanges || saveInProgressRef.current}
            >
              <Save size={16} />
              {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
            </button>

            <button 
              onClick={() => setShowSettingsDialog(true)}
              className={`px-3 py-1.5 text-sm ${
                isDarkMode 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-600 hover:bg-gray-50'
              } rounded-lg transition-colors flex items-center gap-2`}>
              <Settings size={16} />
              Settings
            </button>
            
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="px-3 py-1.5 text-sm text-white bg-green-500 rounded-lg hover:bg-green-600 
                  dark:bg-green-600 dark:hover:bg-green-700 transition-colors flex items-center gap-2"
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export
                  </>
                )}
              </button>
              
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50 py-1 border border-gray-100 dark:border-gray-700">
                  {/* CSV Export Options */}
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    CSV Format
                  </div>
                  <button 
                    onClick={() => {
                      downloadCSV(currentTableData, tableName ? `${tableName}.csv` : 'table.csv');
                      setShowExportDropdown(false);
                    }}
                    disabled={!currentTableData}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Table size={16} />
                    Export Current Table
                  </button>
                  <button 
                    onClick={() => {
                      exportAllTables();
                      setShowExportDropdown(false);
                    }}
                    disabled={!projectData?.files?.length}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Download size={16} />
                    Export All Tables
                  </button>
                  
                  {/* JSON Export Options */}
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-t border-b dark:border-gray-700 mt-1">
                    JSON Format
                  </div>
                  <button 
                    onClick={() => {
                      downloadJSON(currentTableData);
                      setShowExportDropdown(false);
                    }}
                    disabled={!currentTableData}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Table size={16} />
                    Export Current Table
                  </button>
                  <button 
                    onClick={() => {
                      exportAllTablesAsJSON();
                      setShowExportDropdown(false);
                    }}
                    disabled={!projectData?.files?.length}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    <Download size={16} />
                    Export All Tables
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-w-0 relative">
        {/* Only render viewer if not collapsed */}
        <div 
          className={`${viewerCollapsed ? 'w-12' : 'w-1/2'} flex flex-col transition-all duration-300 ease-in-out`}
          style={{ flexShrink: viewerCollapsed ? '0' : '1' }}
        >
          <div className={`h-12 border-b ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100'} 
            flex items-center justify-between px-4`}>
            <span className={`font-medium text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} ${viewerCollapsed ? 'hidden' : ''}`}>
              {(() => {
                const currentFile = fileId 
                  ? projectData?.files?.find(f => f.id === fileId)
                  : projectData?.files?.[0];
                const fileType = currentFile?.fileType;
                
                if (fileType === 'pdf') return 'Original PDF';
                if (fileType && ['jpeg', 'jpg', 'png', 'webp', 'tif'].includes(fileType.toLowerCase())) {
                  return `Original ${fileType.toUpperCase()}`;
                }
                return 'Original File';
              })()}
            </span>
            
            {/* Collapse/Expand button */}
            <button
              onClick={toggleViewerCollapse}
              className={`p-1.5 ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'} 
                rounded-lg transition-colors ml-auto`}
              title={viewerCollapsed ? "Expand viewer" : "Collapse viewer"}
            >
              {viewerCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
          <div className={`flex-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} overflow-hidden relative ${viewerCollapsed ? 'hidden' : ''}`}>
            <div className="absolute inset-0 overflow-auto">
              {renderContentViewer()}
            </div>
          </div>
          
          {/* Collapsed state - vertical button bar */}
          {viewerCollapsed && (
            <div className={`flex-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} flex flex-col items-center py-4`}>
              <button
                onClick={toggleViewerCollapse}
                className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} 
                  shadow-md transition-colors mb-3`}
                title="Expand viewer"
              >
                <ChevronRight size={20} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
              <div className="writing-mode-vertical-lr transform rotate-180 text-xs text-center mt-4">
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  {(() => {
                    const currentFile = fileId 
                      ? projectData?.files?.find(f => f.id === fileId)
                      : projectData?.files?.[0];
                    const fileType = currentFile?.fileType;
                    
                    if (fileType === 'pdf') return 'PDF Viewer';
                    if (fileType && ['jpeg', 'jpg', 'png', 'webp', 'tif'].includes(fileType.toLowerCase())) {
                      return 'Image Viewer';
                    }
                    return 'File Viewer';
                  })()}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={`${viewerCollapsed ? 'flex-1' : 'w-1/2'} flex flex-col ${isDarkMode ? 'border-gray-700' : ''} border-l h-full transition-all duration-300`}>
          <div className={`border-b ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-white'}`}>
            <div className="h-12 px-4 flex items-center gap-4">
              <div className={`flex items-center gap-2 border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pr-4`}>
                {/* Row Operations */}
                <div className="relative" ref={rowOptionsRef}>
                  <button 
                    onClick={() => setShowRowOptions(!showRowOptions)}
                    disabled={selectedCell.row === null}
                    className={`p-1.5 ${
                      isDarkMode 
                        ? selectedCell.row === null 
                          ? 'text-gray-600' 
                          : 'hover:bg-gray-800 text-gray-300'
                        : selectedCell.row === null 
                          ? 'text-gray-300' 
                          : 'hover:bg-gray-100 text-gray-600'
                    } rounded-lg transition-colors group relative`}
                    title="Add Row"
                  >
                    <Plus size={16} className="-rotate-90" />
                    <span className="sr-only">Add Row</span>
                  </button>

                  {/* Row options dropdown */}
                  {showRowOptions && (
                    <div 
                      className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded border border-gray-200 dark:border-gray-700 z-50 w-32"
                      onMouseEnter={() => setRowOptionsHovered(true)}
                      onMouseLeave={() => {
                        setRowOptionsHovered(false);
                        setShowRowOptions(false);
                      }}
                    >
                      <button
                        onClick={() => addRow('above')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        Add above
                      </button>
                      <button
                        onClick={() => addRow('below')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        Add below
                      </button>
                    </div>
                  )}
                </div>
                <button 
                  onClick={deleteCurrentRow}
                  disabled={selectedCell.row === null}
                  className={`p-1.5 ${
                    isDarkMode 
                      ? selectedCell.row === null 
                        ? 'text-gray-600' 
                        : 'hover:bg-gray-800 text-gray-300'
                      : selectedCell.row === null 
                        ? 'text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                  } rounded-lg transition-colors`}
                  title="Delete Current Row"
                >
                  <Trash2 size={16} className="-rotate-90" />
                  <span className="sr-only">Delete Row</span>
                </button>

                {/* Column Operations */}
                <div className="relative" ref={columnOptionsRef}>
                  <button 
                    onClick={() => setShowColumnOptions(!showColumnOptions)}
                    disabled={selectedCell.col === null}
                    className={`p-1.5 ${
                      isDarkMode 
                        ? selectedCell.col === null 
                          ? 'text-gray-600' 
                          : 'hover:bg-gray-800 text-gray-300'
                        : selectedCell.col === null 
                          ? 'text-gray-300' 
                          : 'hover:bg-gray-100 text-gray-600'
                    } rounded-lg transition-colors`}
                    title="Add Column"
                  >
                    <Plus size={16} />
                    <span className="sr-only">Add Column</span>
                  </button>
                  
                  {/* Column options dropdown */}
                  {showColumnOptions && (
                    <div 
                      className="absolute left-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded border border-gray-200 dark:border-gray-700 z-50 w-32"
                      onMouseEnter={() => setColumnOptionsHovered(true)}
                      onMouseLeave={() => {
                        setColumnOptionsHovered(false);
                        setShowColumnOptions(false);
                      }}
                    >
                      <button
                        onClick={() => addColumn('left')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        Add left
                      </button>
                      <button
                        onClick={() => addColumn('right')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        Add right
                      </button>
                    </div>
                  )}
                </div>
                <button 
                  onClick={deleteCurrentColumn}
                  disabled={selectedCell.col === null}
                  className={`p-1.5 ${
                    isDarkMode 
                      ? selectedCell.col === null 
                        ? 'text-gray-600' 
                        : 'hover:bg-gray-800 text-gray-300'
                      : selectedCell.col === null 
                        ? 'text-gray-300' 
                        : 'hover:bg-gray-100 text-gray-600'
                  } rounded-lg transition-colors`}
                  title="Delete Current Column"
                >
                  <Trash2 size={16} />
                  <span className="sr-only">Delete Column</span>
                </button>
              </div>

              <div className={`flex items-center gap-4`}>
                <div className={`flex items-center gap-2 border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pr-4`}>
                  <button 
                    onClick={mergeCells}
                    disabled={!currentSelection?.selectedCells || currentSelection?.selectedCells.length <= 1}
                    className={`p-1.5 ${
                      isDarkMode 
                        ? (!currentSelection?.selectedCells || currentSelection?.selectedCells.length <= 1)
                          ? 'text-gray-600 cursor-not-allowed' 
                          : 'hover:bg-gray-800 text-gray-300'
                        : (!currentSelection?.selectedCells || currentSelection?.selectedCells.length <= 1)
                          ? 'text-gray-300 cursor-not-allowed' 
                          : 'hover:bg-gray-100 text-gray-600'
                    } rounded-lg transition-colors`}
                    title="Merge Selected Cells"
                  >
                    <Merge size={16} />
                    <span className="sr-only">Merge Cells</span>
                  </button>
                </div>  
              </div>

              <div className={`flex items-center gap-4`}>
                <div className={`flex items-center gap-2 border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} pr-4`}>
                  {/* Color buttons */}
                  {[
                    { color: '#f44336', label: 'Red' },
                    { color: '#2196f3', label: 'Blue' },
                    { color: '#ffeb3b', label: 'Yellow' },
                    { color: '#4caf50', label: 'Green' }
                  ].map(({ color, label }) => (
                    <button
                      key={color}
                      onClick={() => applyHighlight(color)}
                      disabled={currentSelection?.range?.start?.row === null}
                      className={`w-6 h-6 rounded transition-transform ${
                        currentSelection?.range?.start?.row === null ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                      title={`Highlight ${label}`}
                    />
                  ))}

                  <button
                    onClick={removeHighlights}
                    disabled={currentSelection?.range?.start?.row === null}
                    className={`p-1.5 ${
                      isDarkMode 
                        ? !currentSelection?.range?.start?.row === null
                          ? 'text-gray-600'
                          : 'hover:bg-gray-800 text-gray-300'
                        : !currentSelection?.range?.start?.row === null
                          ? 'text-gray-300'
                          : 'hover:bg-gray-100 text-gray-600'
                    } rounded-lg transition-colors`}
                    title="Remove Highlight"
                  >
                    <Ban size={16} />
                  </button>
                </div>
              </div>
              <div className={`flex items-center gap-4`}>
                <button 
                  onClick={openQuickDrawEditor}
                  disabled={!hasQuickDrawEligibleCells || quickDrawLoading}
                  className={`p-1.5 ${
                    isDarkMode 
                      ? hasQuickDrawEligibleCells && !quickDrawLoading
                        ? 'hover:bg-gray-800 text-gray-300' 
                        : 'text-gray-600 cursor-not-allowed'
                      : hasQuickDrawEligibleCells && !quickDrawLoading
                        ? 'hover:bg-gray-100 text-gray-600' 
                        : 'text-gray-300 cursor-not-allowed'
                  } rounded-lg transition-colors flex items-center justify-center`}
                  title={
                    quickDrawLoading 
                      ? "Loading..." 
                      : hasQuickDrawEligibleCells 
                        ? "Quick Draw Editing Mode" 
                        : "No cells requiring review (mark cells with red highlight first)"
                  }
                >
                  {quickDrawLoading ? (
                    <div className="animate-spin h-4 w-4 border-2 border-t-transparent border-gray-400 rounded-full" />
                  ) : (
                    <Flame size={16} />
                  )}
                  <span className="sr-only">Quick Draw Edit</span>
                </button>
              </div>

              <div className="relative">
                <button 
                  onClick={() => setShowThresholdSlider(!showThresholdSlider)}
                  className={`p-1.5 ${
                    isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-300' 
                      : 'hover:bg-gray-100 text-gray-600'
                  } rounded-lg transition-colors flex items-center gap-1`}
                  title="Set confidence threshold"
                >
                  <span className="text-xs whitespace-nowrap">Threshold: {confidenceThreshold}%</span>
                </button>
                
                {showThresholdSlider && (
                  <div className={`absolute left-0 top-full mt-2 p-3 rounded-lg shadow-lg z-50 ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white'
                  } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} w-72`}>
                    <div className="mb-3 flex justify-between items-center">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Confidence Threshold: {tempThreshold}%
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">55%</span>
                      <input
                        type="range"
                        min="55"
                        max="90"
                        value={tempThreshold}
                        onChange={(e) => setTempThreshold(parseInt(e.target.value))}
                        className="flex-1 h-2 rounded-lg appearance-none cursor-pointer 
                                  bg-gray-300 dark:bg-gray-600"
                      />
                      <span className="text-xs text-gray-500">90%</span>
                    </div>
                    
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        onClick={cancelThresholdChange}
                        className={`px-3 py-1 text-xs rounded ${
                          isDarkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={applyConfidenceThreshold}
                        className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-grow"></div>
  
              {/* Add Reset button at the right end of the toolbar */}
              <button 
                onClick={resetTableData}
                disabled={!currentTableData}
                className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 ${
                  !currentTableData
                    ? 'opacity-50 cursor-not-allowed'
                    : isDarkMode 
                      ? 'bg-red-900/20 text-red-400 hover:bg-red-900/40' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
                title="Reset to original OCR data"
              >
                <RefreshCcw size={16} />
                <span className="text-xs font-medium whitespace-nowrap ml-1">Reset Table</span>
              </button>

            </div>

            {/* Formula/Text Editor Bar */}
            <div className={`h-8 px-4 border-t ${
              isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'
            } flex items-center gap-2`}>
              <div className={`w-20 px-2 py-1 text-sm ${
                isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-white border'
              } rounded`}>
                {selectedCell.row !== null && selectedCell.col !== null 
                  ? `${String.fromCharCode(65 + selectedCell.col)}${selectedCell.row + 1}`
                  : 'No cell'}
              </div>
              <input
                type="text"
                value={formulaBarValue}
                onChange={(e) => {
                  setFormulaBarValue(e.target.value);
                  if (selectedCell.row !== null && selectedCell.col !== null) {
                    handleCellChange(selectedCell.row, selectedCell.col, e.target.value);
                  }
                }}
                placeholder="Enter cell content"
                className={`flex-1 px-2 py-1 text-sm ${
                  isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-50'
                } border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400`}
                disabled={selectedCell.row === null || selectedCell.col === null}
              />
            </div>
          </div>

          <div className="relative flex-1 overflow-hidden" style={{ height: 'calc(100vh - 8.5rem)' }}> {/* 8.5rem accounts for header + toolbar */}
            <div className="absolute inset-0 overflow-auto"> {/* pb-48 for thumbnail bar */}
              <div className="inline-block min-w-full">
                {renderGrid()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`border-t thumbnail-bar ${
                        isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-white'
                      } transition-all duration-300 fixed bottom-0 left-0 right-0 z-10 ${sidebarOpen ? 'h-64' : 'h-12'}`}>
        <div className={`h-12 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} flex items-center justify-between px-4`}>
          <div className="flex items-center gap-2">
            <Table size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-400'} />
            <span className={`font-medium text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Tables</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-1 ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
          >
            {sidebarOpen ? (
              <ChevronDown size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-400'} />
            ) : (
              <ChevronUp size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-400'} />
            )}
          </button>
        </div>

        {/* Modify the thumbnail bar section only */}
        {sidebarOpen && (
          <div className="overflow-x-auto p-4 whitespace-nowrap">
            <div className="flex gap-4">
              {projectData?.type === 'pdf' ? (
                // Original PDF project thumbnails 
                tables.map((table) => (
                  <button
                    key={table.id}
                    onClick={() => handleTableSelect(table.id)}
                    className={`flex-none text-left transition-all ${
                      selectedTable === table.id
                        ? isDarkMode 
                          ? 'bg-gray-800 border-blue-500'
                          : 'bg-blue-50 border-blue-500'
                        : isDarkMode
                          ? 'hover:bg-gray-800 border-transparent'
                          : 'hover:bg-gray-50 border-transparent'
                    } border rounded-lg p-3 w-48`}
                  >
                    {/* Original table thumbnail code for PDFs */}
                    <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} border ${
                      isDarkMode ? 'border-gray-700' : ''} rounded-md h-20 mb-2 flex items-center justify-center text-gray-400`}>
                      <Table size={24} />
                    </div>
                    <div className="space-y-1">
                      <div className={`font-medium text-sm truncate ${
                        isDarkMode ? 'text-gray-300' : ''
                      }`}>
                        {table.caption?.text || `Table ${table.structure?.rowCount}x${table.structure?.columnCount}`}
                      </div>
                      <div className={`flex items-center justify-between text-xs ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span>Page {table.pageNumber}</span>
                        <span>{table.structure?.rowCount}x{table.structure?.columnCount}</span>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                // Snippet project thumbnails - organized by image with table indicators
                imageUrls.map((image, index) => {
                  // Find all tables for this image
                  const imageTables = projectData?.files?.find(f => f.id === image.id)?.tables || [];
                  
                  if (imageTables.length === 0) {
                    // Image with no tables yet
                    return (
                      <button
                        key={image.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-none text-left transition-all ${
                          currentImageIndex === index
                            ? isDarkMode 
                              ? 'bg-gray-800 border-blue-500'
                              : 'bg-blue-50 border-blue-500'
                            : isDarkMode
                              ? 'hover:bg-gray-800 border-transparent'
                              : 'hover:bg-gray-50 border-transparent'
                        } border rounded-lg p-3 w-48 relative`}
                      >
                        <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} border ${
                          isDarkMode ? 'border-gray-700' : ''} rounded-md h-20 mb-2 flex items-center justify-center overflow-hidden relative`}>
                          {image.url ? (
                            <img 
                              src={image.url} 
                              alt={image.name} 
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <ImageIcon size={24} className="text-gray-400" />
                          )}
                          
                          {/* Show processing status */}
                          {image.status === 'pending' && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <div className="text-white text-xs">Processing...</div>
                            </div>
                          )}
                        </div>
                        <div className="text-sm font-medium truncate">
                          {image.name.split('.')[0]}
                        </div>
                        <div className="text-xs text-gray-500 flex justify-between">
                          <span>No tables yet</span>
                        </div>
                      </button>
                      
                      
                    );
                  } else {
                    // Image with tables - repeat the thumbnail for each table
                    return imageTables.map((table, tableIndex) => (
                      <button
                        key={`${image.id}-${table.id}`}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setSelectedTable(table.id);
                        }}
                        className={`flex-none text-left transition-all ${
                          currentImageIndex === index && selectedTable === table.id
                            ? isDarkMode 
                              ? 'bg-gray-800 border-blue-500'
                              : 'bg-blue-50 border-blue-500'
                            : isDarkMode
                              ? 'hover:bg-gray-800 border-transparent'
                              : 'hover:bg-gray-50 border-transparent'
                        } border rounded-lg p-3 w-48 relative`}
                      >
                        <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} border ${
                          isDarkMode ? 'border-gray-700' : ''} rounded-md h-20 mb-2 flex items-center justify-center overflow-hidden relative`}>
                          <img 
                            src={image.url} 
                            alt={image.name} 
                            className="h-full w-full object-cover opacity-60"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black/60 px-2 py-1 rounded-md text-white text-xs">
                              Table {tableIndex + 1}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-medium truncate dark:text-white">
                          {table.caption?.text || `${image.name.split('.')[0]} - Table ${tableIndex + 1}`}
                        </div>
                        <div className="text-xs text-gray-500 flex justify-between">
                          <span>{table.structure?.rowCount}x{table.structure?.columnCount}</span>
                        </div>
                      </button>
                      
                    ));
                  }
                }).flat() // Flatten the array of arrays
              )}
              {projectData?.type === 'snippet' && (
                <button
                  disabled
                  onClick={() => setShowUploadImageDialog(true)}
                  className="flex-none text-left transition-all hover:bg-gray-50 dark:hover:bg-gray-800 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3 w-48 flex flex-col items-center justify-center"
                >
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-full h-20 w-20 mb-2 flex items-center justify-center text-gray-400`}>
                    <Plus size={44} />
                  </div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Add Image
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
        {/* Quick Draw Editor */}
        {quickDrawMode && (
          <QuickDrawEditor
            key={quickDrawKey} // Add this key to force remount when opened
            tableData={{
              ...currentTableData,
              textractMetadata: {
                ...currentTableData.textractMetadata,
                confidenceThreshold: confidenceThreshold
              }
            }}
            onClose={() => setQuickDrawMode(false)}
            onUpdate={handleQuickDrawUpdate}
            isDarkMode={isDarkMode}
            projectType={projectData?.type || 'pdf'}
            pdfUrl={pdfUrl}
            imageUrl={imageUrls[currentImageIndex]?.url}
            imageId={imageUrls[currentImageIndex]?.id}
            includeBlankCells={includeBlankCells}
          />
        )}
        {showUploadImageDialog && (
          <UploadImageDialog
            isOpen={showUploadImageDialog}
            onClose={() => setShowUploadImageDialog(false)}
            projectId={projectId}
            onImagesUploaded={handleImagesUploaded}
          />
        )}
        {/* Removed old file select modal - using inline dropdown in breadcrumb now */}
        {showSettingsDialog && (
          <WorkspaceSettingsDialog
            isOpen={showSettingsDialog}
            onClose={() => setShowSettingsDialog(false)}
            includeBlankCells={includeBlankCells}
            onToggleBlankCells={updateIncludeBlankCells}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            autoHideThumbnailBar={autoHideThumbnailBar}
            onToggleAutoHideThumbnailBar={updateAutoHideThumbnailBar}
            includeOriginalImages={includeOriginalImages}
            onToggleIncludeOriginalImages={updateIncludeOriginalImages}  
          />
        )}
      </div>
    </div>
  );
};

export default Workspace;